import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, AlertOctagon, XCircle, LogOut, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

const STATUS = {
  fresh: { label: "Price verified", icon: CheckCircle2, cls: "bg-primary/10 text-primary" },
  review: { label: "Review due (30+ days)", icon: AlertTriangle, cls: "bg-amber-100 text-amber-800" },
  urgent: { label: "Urgent review (40+ days)", icon: AlertOctagon, cls: "bg-orange-100 text-orange-800" },
  expired: { label: "Hidden — verify now (45+ days)", icon: XCircle, cls: "bg-red-100 text-red-800" },
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      onLogin();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setBusy(false);
    }
  };
  const field = "w-full rounded-lg border border-sandline bg-white px-4 py-3 text-sm";
  return (
    <main className="flex min-h-screen items-center justify-center bg-sand px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-lg border border-sandline bg-white p-8" data-testid="admin-login-form">
        <p className="font-serif text-2xl font-semibold text-primary">The Perfect Break</p>
        <p className="mt-1 text-sm text-ink-muted">Admin sign in</p>
        <label className="mt-6 block text-sm font-semibold" htmlFor="admin-email">Email</label>
        <input id="admin-email" type="email" required className={`${field} mt-1.5`} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
        <label className="mt-4 block text-sm font-semibold" htmlFor="admin-password">Password</label>
        <input id="admin-password" type="password" required className={`${field} mt-1.5`} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
        <button type="submit" disabled={busy} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="admin-login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

function EditPanel({ brk, onSaved }) {
  const [form, setForm] = useState({
    guide_price: brk.guide_price ?? "",
    price_basis: brk.price_basis || "for two",
    spe_deep_link: brk.spe_deep_link || "",
    featured: !!brk.featured,
  });
  const save = async () => {
    try {
      await api.put(`/admin/breaks/${brk.id}`, { ...form, guide_price: form.guide_price === "" ? null : Number(form.guide_price) });
      toast.success("Saved");
      onSaved();
    } catch {
      toast.error("Save failed");
    }
  };
  const field = "w-full rounded-md border border-sandline bg-white px-3 py-2 text-sm";
  return (
    <div className="mt-4 grid gap-3 rounded-md bg-sand p-4 sm:grid-cols-2" data-testid={`edit-panel-${brk.slug}`}>
      <div>
        <label className="text-xs font-semibold text-ink-muted">Guide price (£, for two)</label>
        <input type="number" className={`${field} mt-1`} value={form.guide_price} onChange={(e) => setForm({ ...form, guide_price: e.target.value })} data-testid={`edit-price-${brk.slug}`} />
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-muted">Price basis</label>
        <input className={`${field} mt-1`} value={form.price_basis} onChange={(e) => setForm({ ...form, price_basis: e.target.value })} data-testid={`edit-basis-${brk.slug}`} />
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs font-semibold text-ink-muted">SPE deep link (booking destination)</label>
        <input className={`${field} mt-1`} value={form.spe_deep_link} onChange={(e) => setForm({ ...form, spe_deep_link: e.target.value })} data-testid={`edit-link-${brk.slug}`} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#1A362D]" data-testid={`edit-featured-${brk.slug}`} />
        Featured on homepage
      </label>
      <div className="flex items-end justify-end">
        <button onClick={save} className="btn-primary !px-5 !py-2" data-testid={`edit-save-${brk.slug}`}>Save</button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [data, setData] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [overview, subs, msgs] = await Promise.all([api.get("/admin/overview"), api.get("/admin/subscribers"), api.get("/admin/contacts")]);
    setData(overview.data);
    setSubscribers(subs.data);
    setContacts(msgs.data);
  };

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(async () => {
        try {
          await api.post("/auth/refresh");
          const r = await api.get("/auth/me");
          setUser(r.data);
        } catch {
          setUser(null);
        }
      });
  }, []);

  useEffect(() => {
    if (user) load().catch(() => toast.error("Couldn't load admin data"));
  }, [user]);

  if (user === undefined) return <main className="flex min-h-screen items-center justify-center bg-sand"><p className="text-ink-muted">Loading…</p></main>;
  if (!user) return <Login onLogin={() => api.get("/auth/me").then((r) => setUser(r.data))} />;

  const runSync = async () => {
    toast.info("Checking SPE pages…");
    try {
      const { data: res } = await api.post("/admin/sync-now");
      const bad = res.results.filter((r) => r.status !== "ok");
      if (bad.length) toast.warning(`Sync done — ${bad.length} item(s) need attention`);
      else toast.success("All SPE links and content checked — all good");
      load();
    } catch {
      toast.error("Sync failed");
    }
  };

  const verify = async (id) => {
    await api.post(`/admin/breaks/${id}/verify-price`);
    toast.success("Price marked as verified today");
    load();
  };

  const markRead = async (id) => {
    await api.post(`/admin/contacts/${id}/read`);
    load();
  };

  const pruneExperiences = async () => {
    if (!window.confirm("Remove venues SPE no longer offers in either package from the site? This updates the live Experiences page immediately.")) return;
    try {
      const r = await api.post("/admin/experiences/prune");
      toast.success(r.data.removed?.length ? `Removed: ${r.data.removed.join(", ")}` : "Nothing to remove — already matches SPE.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove venues");
    }
  };

  const applyLineup = async (b) => {
    const differs = b.spe_experiences_changed || b.spe_lineup_differs;
    const msg = differs
      ? `Replace "${b.name}" experience list with SPE's current line-up? This updates the live break page immediately.`
      : `"${b.name}" already matches SPE's last sync. Re-apply anyway? Safe to run — no changes expected.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/admin/breaks/${b.id}/accept-spe-lineup`);
      const parts = [`Line-up applied — now showing ${r.data.total} experiences.`];
      if (r.data.added?.length) parts.push(`Added: ${r.data.added.join(", ")}.`);
      if (r.data.removed?.length) parts.push(`Removed: ${r.data.removed.join(", ")}.`);
      toast.success(parts.join(" "));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply the SPE line-up");
    }
  };

  return (
    <main className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <div className="border-b border-sandline bg-white">
        <div className="container-tpb flex h-14 items-center justify-between">
          <p className="font-serif text-lg font-semibold text-primary">TPB Admin</p>
          <button
            onClick={async () => { await api.post("/auth/logout"); setUser(null); }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-accent"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
      <div className="container-tpb py-10">
        <h1 className="font-sans text-2xl font-bold" data-testid="admin-heading">Marketing &amp; merchandising</h1>

        <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="admin-stats">
          {[
            { label: "Email subscribers", value: data?.subscribers ?? "…" },
            { label: "Outbound booking clicks", value: data?.outbound_clicks ?? "…" },
            { label: "New contact messages", value: data?.contacts ?? "…" },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-sandline bg-white p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-xs font-semibold uppercase tracking-widest text-ink-muted">{s.label}</p>
              <p className="mt-2 font-serif text-4xl font-semibold text-primary">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sandline bg-white p-5" data-testid="spe-sync-bar">
          <div>
            <h2 className="font-sans text-lg font-bold">SPE nightly sync</h2>
            <p className="text-sm text-ink-muted" data-testid="spe-sync-last">
              {data?.last_sync
                ? `Last run: ${new Date(data.last_sync.ran_at).toLocaleString("en-GB")} — checks every booking link and flags content or price changes on SPE (runs nightly, never changes your copy).`
                : "First sync runs shortly after launch, then nightly."}
            </p>
            {data?.venue_count_spe != null && (
              <p className="mt-1.5 text-sm text-ink-muted" data-testid="venue-count-line">
                SPE currently lists <strong className="text-ink">{data.venue_count_spe}</strong> bookable venues across both packages — this site shows <strong className="text-ink">{data.venue_count_site}</strong>.
                {data.venue_count_mismatch && (
                  <span className="mt-1 flex flex-wrap items-center gap-2 font-semibold text-amber-800" data-testid="venue-count-flag">
                    <span>
                      Venue count differs — review: {[
                        data.venue_new_on_spe?.length ? `new on SPE: ${data.venue_new_on_spe.join(", ")}` : null,
                        data.venue_missing_from_spe?.length ? `on site but not in current SPE packages: ${data.venue_missing_from_spe.join(", ")}` : null,
                      ].filter(Boolean).join(" · ")}
                    </span>
                    {data.venue_missing_from_spe?.length > 0 && (
                      <button
                        onClick={pruneExperiences}
                        className="rounded-full bg-amber-800 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-amber-900"
                        data-testid="prune-experiences-btn"
                      >
                        Remove from site
                      </button>
                    )}
                  </span>
                )}
              </p>
            )}
          </div>
          <button onClick={runSync} className="btn-primary !px-5 !py-2.5" data-testid="run-sync-button">
            <RefreshCw className="h-4 w-4" /> Run sync now
          </button>
        </div>

        <h2 className="mt-12 font-sans text-lg font-bold" data-testid="price-review-heading">
          Breaks &amp; guide prices
          <span className="ml-2 text-sm font-normal text-ink-muted">warning at 30 days · urgent at 40 · price hidden at 45 until verified</span>
        </h2>
        <div className="mt-4 space-y-4">
          {data?.breaks.map((b) => {
            const st = STATUS[b.price_status] || STATUS.fresh;
            return (
              <div key={b.id} className="rounded-lg border border-sandline bg-white p-5" data-testid={`admin-break-${b.slug}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-ink">{b.name}</p>
                    <p className="text-sm text-ink-muted">
                      {b.guide_price != null ? `From £${b.guide_price} ${b.price_basis}` : "No guide price"}
                      {b.days_since_review != null && ` · verified ${b.days_since_review} day${b.days_since_review === 1 ? "" : "s"} ago`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => applyLineup(b)}
                      className={(b.spe_experiences_changed || b.spe_lineup_differs)
                        ? "flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-widest text-sand transition-colors hover:bg-primary/90"
                        : "flex items-center gap-1.5 rounded-full border border-sandline px-4 py-2 text-xs font-bold uppercase tracking-widest text-ink-muted transition-colors hover:border-primary hover:text-primary"}
                      data-testid={`accept-lineup-${b.slug}`}
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> {(b.spe_experiences_changed || b.spe_lineup_differs) ? "Apply SPE line-up" : "Re-apply SPE line-up"}
                    </button>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${st.cls}`} data-testid={`price-status-${b.slug}`}>
                      <st.icon className="h-3.5 w-3.5" /> {st.label}
                    </span>
                    {b.spe_link_status === "broken" && <span className="rounded-full bg-red-100 px-3 py-1.5 text-xs font-bold text-red-800" data-testid={`spe-link-${b.slug}`}>SPE link broken</span>}
                    {b.spe_link_status === "unknown" && <span className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800" data-testid={`spe-link-${b.slug}`}>SPE check inconclusive</span>}
                    {b.spe_content_changed && <span className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800" data-testid={`spe-changed-${b.slug}`}>SPE content changed — review</span>}
                    {b.spe_experiences_changed && <span className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800" data-testid={`spe-experiences-${b.slug}`}>SPE experience line-up changed — review</span>}
                    {b.spe_price_mismatch && <span className="rounded-full bg-red-100 px-3 py-1.5 text-xs font-bold text-red-800" data-testid={`spe-price-${b.slug}`}>SPE price differs{b.spe_detected_prices?.length ? `: £${b.spe_detected_prices.join(" / £")}` : ""}</span>}
                    <button onClick={() => verify(b.id)} className="inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-1.5 text-xs font-bold text-primary transition-colors duration-300 hover:bg-primary hover:text-sand" data-testid={`verify-price-${b.slug}`}>
                      <RefreshCw className="h-3.5 w-3.5" /> Verified today
                    </button>
                    <button onClick={() => setEditing(editing === b.id ? null : b.id)} className="rounded-full border border-sandline px-4 py-1.5 text-xs font-bold text-ink transition-colors duration-300 hover:border-primary" data-testid={`edit-break-${b.slug}`}>
                      {editing === b.id ? "Close" : "Edit"}
                    </button>
                  </div>
                </div>
                {editing === b.id && <EditPanel brk={b} onSaved={() => { setEditing(null); load(); }} />}
              </div>
            );
          })}
        </div>

        <h2 className="mt-12 font-sans text-lg font-bold" data-testid="contacts-heading">Contact messages ({contacts.filter((c) => c.status === "new").length} new)</h2>
        <div className="mt-4 space-y-3">
          {contacts.length === 0 && (
            <div className="rounded-lg border border-sandline bg-white p-6 text-sm text-ink-muted" data-testid="contacts-empty">
              No messages yet — enquiries from the contact page land here.
            </div>
          )}
          {contacts.map((c, i) => (
            <div key={c.id} className="rounded-lg border border-sandline bg-white p-5" data-testid={`contact-item-${i}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="font-semibold text-ink">{c.name}</p>
                <a href={`mailto:${c.email}`} className="text-sm text-primary underline underline-offset-2" data-testid={`contact-email-${i}`}>{c.email}</a>
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${c.status === "new" ? "bg-amber-100 text-amber-800" : "bg-sand-dark text-ink-muted"}`} data-testid={`contact-status-${i}`}>
                  {c.status === "new" ? "New" : "Read"}
                </span>
                <span className="text-xs text-ink-muted sm:ml-auto">{new Date(c.created_at).toLocaleString("en-GB")}</span>
              </div>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink-muted">{c.message}</p>
              {c.status === "new" && (
                <button
                  onClick={() => markRead(c.id)}
                  className="mt-3 rounded-full border border-sandline px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-ink-muted transition-colors hover:border-primary hover:text-primary"
                  data-testid={`contact-mark-read-${i}`}
                >
                  Mark as read
                </button>
              )}
            </div>
          ))}
        </div>

        <h2 className="mt-12 font-sans text-lg font-bold" data-testid="subscribers-heading">Email subscribers ({subscribers.length})</h2>
        <div className="mt-4 overflow-x-auto rounded-lg border border-sandline bg-white">
          <table className="w-full min-w-[540px] text-left text-sm">
            <thead className="bg-sand-dark text-xs uppercase tracking-widest text-ink-muted">
              <tr><th className="px-5 py-3">Email</th><th className="px-5 py-3">Location</th><th className="px-5 py-3">EmailOctopus</th><th className="px-5 py-3">Joined</th></tr>
            </thead>
            <tbody>
              {subscribers.map((s) => (
                <tr key={s.id} className="border-t border-sandline" data-testid={`subscriber-row-${s.id}`}>
                  <td className="px-5 py-3">{s.email}</td>
                  <td className="px-5 py-3 text-ink-muted">{s.location}</td>
                  <td className="px-5 py-3" data-testid={`subscriber-eo-${s.id}`}>
                    {s.emailoctopus_status === "pending" && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-800">Awaiting confirm</span>}
                    {(s.emailoctopus_status === "subscribed" || s.emailoctopus_status === "already_on_list") && <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold text-primary">Synced</span>}
                    {s.emailoctopus_status === "sync_failed" && <span className="rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-bold text-red-800">Sync failed</span>}
                    {!s.emailoctopus_status && <span className="text-ink-muted">—</span>}
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{new Date(s.created_at).toLocaleDateString("en-GB")}</td>
                </tr>
              ))}
              {subscribers.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-ink-muted" data-testid="subscribers-empty">No subscribers yet — the capture forms are live on the site.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
